import * as Sentry from '@sentry/react';

Sentry.init({
  dsn:
    process.env.REACT_APP_SENTRY_DSN ||
    window?.env?.REACT_APP_SENTRY_DSN ||
    'https://e1d144a2c64ffaf4e6ad1e349c6d4a53@o4508920210063360.ingest.de.sentry.io/4511705454608464',
  environment: window?.env?.REACT_APP_ENV || process.env.REACT_APP_ENV,
  release:
    window?.env?.REACT_APP_SENTRY_RELEASE ||
    process.env.REACT_APP_SENTRY_RELEASE,
});

// Many call sites pass non-Error values to `sendError` — most commonly the
// RTK Query error shape `{ status, data }` (see axiosBaseQuery) or a raw axios
// error `{ response: { status, data } }`. Handing a plain object to
// `captureException` makes Sentry fall back to the useless
// "Object captured as exception with keys: ..." title. Normalise into a real
// Error with a meaningful message, keeping the original payload as context.
const toError = (error) => {
  if (error instanceof Error) return { error };

  const status = error?.status ?? error?.response?.status;
  const data = error?.data ?? error?.response?.data;
  const message =
    (typeof data === 'string' && data) ||
    data?.message ||
    error?.message ||
    (typeof error === 'string' && error) ||
    (status != null
      ? `Request failed with status ${status}`
      : 'Non-error value captured');

  const wrapped = new Error(message);
  wrapped.name = error?.name || 'CapturedNonError';

  const captureContext = {
    extra: { originalError: error },
    ...(status != null && { tags: { http_status: status } }),
  };

  if (typeof error?.stack === 'string' && error.stack.trim()) {
    // Reuse the original throw's stack (preserved by axiosBaseQuery) so Sentry
    // attributes the event to where it actually happened, rather than grouping
    // every non-Error under this toError/sendError frame.
    wrapped.stack = error.stack;
  } else {
    // No stack to anchor on. Fingerprint explicitly so distinct non-Errors
    // don't all collapse into one toError()-anchored issue.
    captureContext.fingerprint = [
      'captured-non-error',
      status != null ? String(status) : wrapped.name,
      message,
    ];
  }

  return { error: wrapped, captureContext };
};

// Backwards-compatible reporter so existing `sentry.sendError(error)` call
// sites keep working after the swap from AppSignal to Sentry.
const sentry = {
  sendError: (error, additionalContext) => {
    const { error: normalised, captureContext = {} } = toError(error);
    if (additionalContext) {
      captureContext.tags = { ...captureContext.tags, ...additionalContext.tags };
      captureContext.extra = { ...captureContext.extra, ...additionalContext.extra };
    }
    Sentry.captureException(normalised, captureContext);
  },
};

export default sentry;
export { Sentry };
